import { useCallback } from "react";

import { isEmpty } from "lodash/fp";

import useTranscript from "@app/analytics/MeetingReport/hooks/useTranscript";
import useTranscriptStore from "@app/analytics/MeetingReport/stores/transcript";
import useMeetingReportUiStore from "@app/analytics/MeetingReport/stores/ui";
import useVideoStore from "@app/analytics/MeetingReport/stores/video";
import { getParagraphAtTimestamp } from "@app/analytics/MeetingReport/utils";
import type { TranscriptTurnParagraph } from "@app/common/types";

interface JumpToTimestampOpts {
  onJumpTo?: () => void;
}

interface ScrollToParagraphOpts {
  type: "paragraph";
  rawTimestamp?: number;
  turns?: TranscriptTurnParagraph[];
  onScrollTo?: () => void;
}

interface ScrollToChapterOpts {
  type: "chapter";
  id: string;
  onScrollTo?: () => void;
}

type ScrollToItemOpts = ScrollToParagraphOpts | ScrollToChapterOpts;

const useReportActions = () => {
  const { turns } = useTranscript();

  const openSidebar = useMeetingReportUiStore((state) => state.openSidebar);
  const setActiveItem = useTranscriptStore((state) => state.setActiveItem);
  const { shouldAutoplayOnClick, setVideoStartAt, videoIsPaused } =
    useVideoStore((state) => ({
      shouldAutoplayOnClick: state.autoPlay && state.playbackEnabled,
      setVideoStartAt: state.setVideoStartAt,
      videoIsPaused: state.paused,
    }));

  const makeJumpToTimestamp = useCallback(
    (timestamp: number, opts?: JumpToTimestampOpts) => {
      if (!shouldAutoplayOnClick) return;
      return () => {
        opts?.onJumpTo?.();
        setVideoStartAt(timestamp);
        // if the video was not already playing, pop open the sidebar so that
        // when it starts playing, it's obvious where the sound is coming from
        if (videoIsPaused) openSidebar();
      };
    },
    [shouldAutoplayOnClick, setVideoStartAt, openSidebar, videoIsPaused]
  );

  const makeScrollToItem = useCallback(
    (opts: ScrollToItemOpts) => {
      if (opts.type === "chapter") {
        return () => {
          opts?.onScrollTo?.();
          setActiveItem({ id: opts.id, type: "chapter" });
        };
      }

      if (opts.type === "paragraph") {
        if (opts.rawTimestamp == null) return;

        const turnsToSearch = opts?.turns ?? turns;
        if (isEmpty(turnsToSearch)) return;
        const paragraph = getParagraphAtTimestamp(
          turnsToSearch,
          opts.rawTimestamp
        );
        if (paragraph) {
          return () => {
            opts?.onScrollTo?.();
            setActiveItem({ id: paragraph.id, type: "paragraph" });
          };
        }
      }
    },
    [turns, setActiveItem]
  );

  return {
    shouldJumpToTimestamp: shouldAutoplayOnClick,
    makeJumpToTimestamp,
    makeScrollToItem,
  };
};

export default useReportActions;
